import { OFFICIAL_USER_ID } from '../../constants/official';
import { apiClient } from '../../services/apiClient';
import { postService, type WorkSummary } from '../../services/postService';
import type { WorkDetail } from '../../components/Profile/work/WorkDetailModal';

export interface StudioOfficialUser {
  id: string;
  name: string;
  avatar: string;
}

interface OfficialInfoResponse {
  success?: boolean;
  data?: {
    officialUser?: {
      userId: string;
      displayName?: string;
      username?: string;
      avatarUrl?: string;
    };
  };
}

export const DEFAULT_OFFICIAL_USER: StudioOfficialUser = {
  id: OFFICIAL_USER_ID,
  name: '云作Studio',
  avatar: '',
};

export const loadOfficialStudioPortfolio = async (): Promise<{
  officialUser: StudioOfficialUser;
  works: WorkSummary[];
}> => {
  let officialUser = DEFAULT_OFFICIAL_USER;

  try {
    const response = await apiClient.get<OfficialInfoResponse>(
      '/api/v1/chat/official',
      undefined,
      { suppressAuthPrompt: true }
    );
    const official = response.data?.data?.officialUser;
    if (official?.userId) {
      officialUser = {
        id: official.userId,
        name: official.displayName || official.username || DEFAULT_OFFICIAL_USER.name,
        avatar: official.avatarUrl || '',
      };
    }
  } catch (error) {
    console.error('Failed to resolve official studio user:', error);
  }

  const works = await postService.getCreatorPosts(officialUser.id, 100, { publicOnly: true });

  return {
    officialUser,
    works: works.filter((work) => (
      work.status === 'active' &&
      work.visibility === 'public' &&
      work.auditStatus === 'APPROVED'
    )),
  };
};

export const createStudioWorkDetail = (
  work: WorkSummary,
  officialUser: StudioOfficialUser
): WorkDetail => ({
  id: work.id,
  type: work.type,
  videoUrl: work.videoUrl,
  images: work.images,
  thumbnailUrl: work.thumbnailUrl,
  title: work.title,
  description: work.description,
  tags: work.tags || [],
  categories: work.categories || [],
  industry: work.industry,
  likes: work.likes,
  views: work.views,
  favorites: work.favorites,
  comments: [],
  commentCount: work.commentsCount,
  createdAt: work.createdAt,
  pinnedAt: work.pinnedAt,
  author: {
    id: work.creator?.id || work.creatorUserId || officialUser.id,
    name: work.creator?.name || officialUser.name,
    avatar: work.creator?.avatar || officialUser.avatar,
  },
  collaborators: work.collaborators,
  isPinned: work.isPinned,
  liked: work.liked,
  bookmarked: work.bookmarked,
  auditStatus: work.auditStatus,
  visibility: work.visibility,
  status: work.status,
});
